import { loadStripe } from '@stripe/stripe-js';
import { useCallback, useEffect, useMemo, useState } from 'react';

import SuartePlusShortIcon from '@/assets/all-icons/suarte-plus-short-icon';
import stripeConfig, { resolveStripePublishableKey } from '@/config/stripe.config';
import useGoBack from '@/hooks/useGoBack';
import useStateHandler from '@/hooks/useStateHandler';
import useTranslations from '@/i18n/useTranslations';
import ChannelsService from '@/services/channels.service';
import gallerySubscriptionService from '@/services/gallerySubscription.service';
import suartePlusService from '@/services/suartePlus.service';
import SupportService from '@/services/support.service';
import ContinueButton from '@/shared-components/buttons/components/ContinueButton';
import Loader from '@/shared-components/loader/Loader';
import ProfileOptionsPageHeader from '@/shared-components/navigation/ProfileOptionsPageHeader';
import GallerySubscriptionPaymentModal from '@/shared-components/popups/components/GallerySubscriptionPaymentModal';
import SuartePlusPopup from '@/shared-components/popups/components/SuartePlusPopup';
import TemporalPopup from '@/shared-components/popups/components/TemporalPopup';
import Text from '@/shared-components/text/components/Text';
import { formatCurrencyAmount } from '@/utils/currency.utils';
import { resolveUserProfileImage, resolveUserProfileTypeWithFallback } from '@/utils/userProfileImage';

import BusinessBillingDialog from './components/BusinessBillingDialog';
import ChannelSubscriptionCard from './components/ChannelSubscriptionCard';
import GallerySubscriptionCta from './components/GallerySubscriptionCta';
import InvoiceDialog from './components/InvoiceDialog';
import PaymentsSection from './components/PaymentsSection';
import PaymentSummaryDialog from './components/PaymentSummaryDialog';
import SuartePlusCancellationDialog from './components/SuartePlusCancellationDialog';
import SuartePlusSubscriptionCard from './components/SuartePlusSubscriptionCard';
import { ChannelRevocationDialog, SupportCancellationDialog } from './components/SubscriptionCancellationDialogs';
import SupportSubscriptionCard from './components/SupportSubscriptionCard';
import useGallerySubscription from './hooks/useGallerySubscription';
import usePaymentUiState from './hooks/usePaymentUiState';
import useProfilePaymentsData from './hooks/useProfilePaymentsData';
import invoicesService from './services/invoices.service';
import { hasBusinessBilling } from './utils/billingMode';
import {
  resolveChannelAvatarProfileType,
} from './utils/channelPresentation';
import {
  buildChannelSubscriptionPaymentEntries,
  CHANNEL_CHECKOUT_CHANNEL_ID_PARAM,
  CHANNEL_CHECKOUT_POLL_INTERVAL_MS,
  CHANNEL_CHECKOUT_POLL_MAX_ATTEMPTS,
  CHANNEL_CHECKOUT_SUCCESS_FLAG,
  coerceDate,
  DEFAULT_GALLERY_PLAN,
  DEFAULT_LIFETIME_CUTOFF,
  extractChannelSubscriptions,
  extractChannelSubscriptionsFromMembershipSummary,
  extractPaymentEntries,
  extractSupportSubscriptions,
  formatGalleryPriceHint,
  formatNextBillingLabel,
  resolveAmountValue,
  resolveCancelAtDate,
  resolveCurrency,
  resolveNextBillingDate,
  resolvePriceAmount,
  resolveSubscriptionIsActive,
} from './utils/data/normalizers';
import { mergeAndSortPaymentEntries } from './utils/mergePaymentEntries';
import { getPaymentFilterOptions, PAYMENT_FILTER_OPTIONS } from './utils/paymentCategories';
import {
  hasVerifiedInvoiceBilling,
} from './utils/paymentEntryState';

import '@/layouts/navigation/components/styles/Subscriptions.css';
import './styles/ProfilePayments.css';

export default function ProfileSubscriptionsPage() {
  const t = useTranslations('common.profile.buttons.payments');
  const { stateHandler } = useStateHandler();
  const {
    profileData, profileLoading, profileError, userPaymentsPayload, userPaymentsLoading,
    userPaymentsError, channelMembershipSummary, channelMembershipLoading, channelMembershipError,
    setChannelMembershipError, suarteInvoicesByOrderId, setSuarteInvoicesByOrderId,
    galleryInvoicesByOrderId, refreshProfile,
  } = useProfilePaymentsData();
  const {
    showSuartePlusPopup, setShowSuartePlusPopup, showGalleryPaymentModal, setShowGalleryPaymentModal,
    showSeatPaymentModal, setShowSeatPaymentModal, showCancelSuartePlusPopup, setShowCancelSuartePlusPopup,
    showSupportCancelPopup, setShowSupportCancelPopup, supportCancellationTarget, setSupportCancellationTarget,
    activeSupportActionMenuId, setActiveSupportActionMenuId, activeChannelActionMenuId, setActiveChannelActionMenuId,
    activePaymentActionMenuId, setActivePaymentActionMenuId, paymentFilter, setPaymentFilter,
    summaryTarget, setSummaryTarget, invoiceTarget, setInvoiceTarget, invoiceStatusMessage, setInvoiceStatusMessage,
    invoiceBillingRequired, setInvoiceBillingRequired, invoiceActionLoadingOrderId, setInvoiceActionLoadingOrderId,
    channelRevokeTarget, setChannelRevokeTarget, channelRevokePopupOpen, setChannelRevokePopupOpen,
    businessBillingTarget, setBusinessBillingTarget,
  } = usePaymentUiState();
  const [isActivatingLifetime, setIsActivatingLifetime] = useState(false);
  const [lifetimeActivationError, setLifetimeActivationError] = useState(null);
  const [isCancellingSuartePlus, setIsCancellingSuartePlus] = useState(false);
  const [isRenewingSuartePlus, setIsRenewingSuartePlus] = useState(false);
  const [suartePlusCancellationError, setSuartePlusCancellationError] = useState(null);
  const [suartePlusRenewalMessage, setSuartePlusRenewalMessage] = useState(null);
  const [supportResumeError, setSupportResumeError] = useState(null);
  const [activatingChannelId, setActivatingChannelId] = useState(null);
  const [revokingChannelSubscription, setRevokingChannelSubscription] = useState(false);
  const [businessBillingError, setBusinessBillingError] = useState(null);
  const [businessBillingLoading, setBusinessBillingLoading] = useState(false);
  const [businessBillingNeedsDetails, setBusinessBillingNeedsDetails] = useState(false);
  const [businessBillingSuccessMessage, setBusinessBillingSuccessMessage] = useState(null);
  const [stripePromise, setStripePromise] = useState(
    stripeConfig.publishableKey ? loadStripe(stripeConfig.publishableKey) : null
  );

  const normalizedUserType = (profileData?.user_type ?? profileData?.__t ?? '').toString().toLowerCase();
  const isGallery = normalizedUserType === 'gallery';
  const canAccessSuartePlus = ['collector', 'artist'].includes(normalizedUserType);
  const hasActiveSuartePlus = canAccessSuartePlus && Boolean(profileData?.suartePlus?.isActive);
  const suartePlusMembershipId = profileData?.suartePlus?.id ?? profileData?.suartePlus?._id ?? null;
  const showSuartePlusCancelButton = hasActiveSuartePlus && !isGallery && Boolean(suartePlusMembershipId);

  const {
    gallerySubscription, galleryLoading, galleryError, galleryConfig, galleryConfigError,
    galleryConfigLoading, galleryCheckoutPrice, setGalleryCheckoutPrice, refreshGallerySubscription,
  } = useGallerySubscription(isGallery);

  const suartePlusMembership = canAccessSuartePlus ? profileData?.suartePlus ?? null : null;
  const isSuartePlusBusinessBilling = useMemo(
    () => hasBusinessBilling(suartePlusMembership),
    [suartePlusMembership]
  );
  const activeSubscription = isGallery ? gallerySubscription : suartePlusMembership;
  const membershipChannelSubscriptions = useMemo(
    () => extractChannelSubscriptionsFromMembershipSummary(channelMembershipSummary).map((subscription) => ({
      ...subscription,
      source: 'membership',
    })),
    [channelMembershipSummary]
  );
  const subscriptionLoading = profileLoading || channelMembershipLoading || (isGallery && galleryLoading);
  const showSuartePlusUpsell = canAccessSuartePlus && !isGallery && !hasActiveSuartePlus && !subscriptionLoading;
  const profileCreatedAtDate = useMemo(
    () => coerceDate(profileData?.createdAt ?? profileData?.created_at),
    [profileData]
  );
  const galleryPlanKey = useMemo(() => {
    const subscriptionPlan = typeof gallerySubscription?.plan === 'string'
      ? gallerySubscription.plan.trim().toLowerCase()
      : null;
    if (subscriptionPlan) {
      return subscriptionPlan;
    }
    if (!galleryConfig?.plans || typeof galleryConfig.plans !== 'object') {
      return DEFAULT_GALLERY_PLAN;
    }
    const defaultPlanKey = typeof galleryConfig.defaultPlan === 'string'
      ? galleryConfig.defaultPlan
      : null;
    if (defaultPlanKey && galleryConfig.plans[defaultPlanKey]) {
      return defaultPlanKey;
    }
    if (galleryConfig.plans.standard) {
      return 'standard';
    }
    const availablePlans = Object.keys(galleryConfig.plans);
    if (availablePlans.length > 0) {
      return availablePlans[0];
    }
    return DEFAULT_GALLERY_PLAN;
  }, [galleryConfig, gallerySubscription?.plan]);
  const galleryLifetimeBenefit = useMemo(() => {
    if (!isGallery) {
      return {
        cutoffDate: null,
        label: null,
        reason: null,
        hasLifetimeSubscription: false,
        qualifiesByDate: false,
        isEligible: false,
      };
    }
    const lifetimeConfig = galleryConfig?.lifetimeFreeAccess ?? {};
    const cutoffRaw =
      lifetimeConfig.cutoff ??
      lifetimeConfig.createdBefore ??
      lifetimeConfig.created_before ??
      DEFAULT_LIFETIME_CUTOFF;
    const cutoffDate = coerceDate(cutoffRaw);
    const createdAt = profileCreatedAtDate instanceof Date ? profileCreatedAtDate : null;
    const qualifiesByDate = Boolean(
      cutoffDate instanceof Date &&
      createdAt instanceof Date &&
      createdAt < cutoffDate
    );
    const hasLifetimeSubscription = Boolean(gallerySubscription?.lifetimeFreeAccess);
    return {
      cutoffDate: cutoffDate instanceof Date ? cutoffDate : null,
      label: typeof lifetimeConfig.label === 'string' ? lifetimeConfig.label : 'Lifetime gallery access',
      reason: typeof lifetimeConfig.reason === 'string'
        ? lifetimeConfig.reason
        : 'Founding galleries keep access free forever.',
      hasLifetimeSubscription,
      qualifiesByDate,
      isEligible: hasLifetimeSubscription || qualifiesByDate,
    };
  }, [galleryConfig, gallerySubscription, isGallery, profileCreatedAtDate]);
  const lifetimeCutoffLabel = useMemo(() => {
    if (!(galleryLifetimeBenefit.cutoffDate instanceof Date)) {
      return 'November 10th, 2025';
    }
    return new Intl.DateTimeFormat('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    }).format(galleryLifetimeBenefit.cutoffDate);
  }, [galleryLifetimeBenefit.cutoffDate]);
  const requiresGalleryPaymentMethod = isGallery && !galleryLifetimeBenefit.isEligible;
  const lifetimeGalleryMessage = useMemo(() => {
    if (!isGallery || !galleryLifetimeBenefit.isEligible) {
      return null;
    }
    const baseMessage = `This gallery joined before ${lifetimeCutoffLabel}.`;
    if (galleryLifetimeBenefit.hasLifetimeSubscription) {
      return `${baseMessage} Base subscription is free forever.`;
    }
    return `${baseMessage} Activate the subscription once to unlock free lifetime access.`;
  }, [galleryLifetimeBenefit.hasLifetimeSubscription, galleryLifetimeBenefit.isEligible, isGallery, lifetimeCutoffLabel]);

  const isPrimarySubscriptionActive = useMemo(
    () => (isGallery ? Boolean(activeSubscription) : resolveSubscriptionIsActive(activeSubscription)),
    [activeSubscription, isGallery]
  );
  const hasPrimarySubscription = isPrimarySubscriptionActive;
  const nextBillingDate = useMemo(() => resolveNextBillingDate(activeSubscription), [activeSubscription]);
  const amountValue = useMemo(() => resolveAmountValue(activeSubscription), [activeSubscription]);
  const currencyCode = useMemo(() => resolveCurrency(activeSubscription), [activeSubscription]);
  const isPastNextBillingDate = Boolean(
    nextBillingDate instanceof Date
    && Number.isFinite(nextBillingDate.getTime())
    && nextBillingDate.getTime() < Date.now()
  );
  const nextBillingLabel = useMemo(() => {
    if (isGallery && galleryLifetimeBenefit.hasLifetimeSubscription) {
      return 'No billing scheduled';
    }
    if (!isGallery && hasActiveSuartePlus && isPastNextBillingDate) {
      return 'Billing date syncing';
    }
    return formatNextBillingLabel(nextBillingDate);
  }, [
    galleryLifetimeBenefit.hasLifetimeSubscription,
    hasActiveSuartePlus,
    isGallery,
    isPastNextBillingDate,
    nextBillingDate,
  ]);
  const cancelAtDate = useMemo(() => resolveCancelAtDate(activeSubscription), [activeSubscription]);
  const isSuartePlusCancellationScheduled =
    cancelAtDate instanceof Date && cancelAtDate.getTime() > Date.now();
  const cancelButtonLabel = isCancellingSuartePlus
    ? 'Cancelling...'
    : isSuartePlusCancellationScheduled
      ? 'Cancelled'
      : 'Cancel';
  const cancellationMessage = useMemo(() => {
    if (!isSuartePlusCancellationScheduled || isGallery) {
      return null;
    }
    return `Subscription will cancel on ${formatNextBillingLabel(cancelAtDate)}.`;
  }, [cancelAtDate, isGallery, isSuartePlusCancellationScheduled]);
  const canShowRenewAction = isSuartePlusCancellationScheduled && !isGallery && canAccessSuartePlus;

  const isFoundingGallery = galleryLifetimeBenefit.qualifiesByDate;
  const hasVerifiedGalleryVat = Boolean(profileData?.gallery_tax_id);
  const galleryBaseAmount = Number(activeSubscription?.price?.amount ?? NaN);
  const galleryVatPercentage = Number(
    activeSubscription?.price?.vatPercentage ?? activeSubscription?.price?.vat_percentage ?? NaN
  );
  const isGalleryVatZero = Number.isFinite(galleryVatPercentage) && galleryVatPercentage === 0;
  const gallerySeats = gallerySubscription?.seats ?? null;
  const seatCurrency = gallerySeats?.currency ?? resolveCurrency(gallerySubscription);
  const seatCostWithVat = resolvePriceAmount({
    totalWithVat: gallerySeats?.totalWithVat ?? gallerySeats?.total_with_vat,
  });
  const seatCostNet = resolvePriceAmount({ amount: gallerySeats?.subtotal });
  const seatCostValue =
    (isGallery && (hasVerifiedGalleryVat || isGalleryVatZero) && Number.isFinite(seatCostNet))
      ? seatCostNet
      : (Number.isFinite(seatCostWithVat) ? seatCostWithVat : null);
  const seatCostLabel = Number.isFinite(seatCostValue)
    ? formatCurrencyAmount(seatCostValue, seatCurrency, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
    : null;
  const seatsCount = Number(gallerySeats?.count ?? 0);
  const seatsBillable = Number.isFinite(Number(gallerySeats?.billable))
    ? Number(gallerySeats?.billable)
    : Math.max(0, seatsCount - Number(gallerySeats?.included ?? 0));
  const seatsSummary = gallerySeats
    ? `${seatsCount} used${seatsBillable > 0 ? ` • ${seatsBillable} paid` : ''}`
    : null;
  const hasSeatPaymentMethod = Boolean(gallerySubscription?.metadata?.stripe_payment_method_id);
  const needsSeatPaymentMethod =
    isGallery &&
    galleryLifetimeBenefit.hasLifetimeSubscription &&
    seatsBillable > 0 &&
    !hasSeatPaymentMethod;
  const displayAmountValue =
    (isGallery && (hasVerifiedGalleryVat || isGalleryVatZero) && Number.isFinite(galleryBaseAmount))
      ? galleryBaseAmount
      : amountValue;
  const galleryBaseAmountLabel = galleryLifetimeBenefit.hasLifetimeSubscription
    ? 'Free'
    : displayAmountValue != null && Number.isFinite(displayAmountValue)
      ? formatCurrencyAmount(displayAmountValue, currencyCode, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
      : null;
  const galleryTotalValue = isGallery && Number.isFinite(displayAmountValue)
    ? displayAmountValue + (Number.isFinite(seatCostValue) ? seatCostValue : 0)
    : null;
  const galleryTotalLabel = Number.isFinite(galleryTotalValue)
    ? formatCurrencyAmount(galleryTotalValue, currencyCode, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
    : null;
  const amountLabel = galleryBaseAmountLabel;
  const galleryPaymentHint = useMemo(() => {
    if (!isGallery) {
      return null;
    }
    if (!requiresGalleryPaymentMethod) {
      return lifetimeGalleryMessage;
    }
    const priceHint = formatGalleryPriceHint(
      galleryCheckoutPrice ?? gallerySubscription?.price ?? null
    );
    if (priceHint) {
      return priceHint;
    }
    if (galleryConfigLoading) {
      return 'Loading gallery subscription price.';
    }
    if (galleryConfigError) {
      return galleryConfigError;
    }
    return 'Stripe will show the subscription price before you confirm payment.';
  }, [
    galleryCheckoutPrice,
    galleryConfig,
    galleryConfigError,
    galleryConfigLoading,
    gallerySubscription,
    isGallery,
    lifetimeGalleryMessage,
    requiresGalleryPaymentMethod,
  ]);

  const paymentsSource = userPaymentsPayload ?? profileData;
  const paymentsLoading = profileLoading || userPaymentsLoading;
  const supportSubscriptions = useMemo(
    () => extractSupportSubscriptions(paymentsSource),
    [paymentsSource]
  );
  const activeSupportSubscriptions = useMemo(
    () => supportSubscriptions.filter((subscription) => subscription.isActive),
    [supportSubscriptions]
  );
  const paymentEntries = useMemo(
    () => extractPaymentEntries(paymentsSource),
    [paymentsSource]
  );
  const payloadChannelSubscriptions = useMemo(
    () => extractChannelSubscriptions(paymentsSource).map((subscription) => ({
      ...subscription,
      source: 'payments',
    })),
    [paymentsSource]
  );
  const activeChannelSubscriptions = useMemo(() => {
    const merged = new Map();
    const isActiveChannelSubscription = (subscription) =>
      Boolean(subscription?.isActive && !subscription?.isCancelled);

    [...membershipChannelSubscriptions, ...payloadChannelSubscriptions].forEach((subscription) => {
      if (!subscription || !subscription.channelId) {
        return;
      }
      const key = String(subscription.channelId);
      const previous = merged.get(key) || {};

      const shouldKeepPreviousStatus =
        isActiveChannelSubscription(previous) && !isActiveChannelSubscription(subscription);

      const nextSubscription = {
        ...previous,
        ...subscription,
        channelId: key,
      };

      if (!nextSubscription.channelKind) {
        nextSubscription.channelKind = previous.channelKind ?? null;
      }

      const previousAvatarType = resolveChannelAvatarProfileType({
        ownerType: previous.ownerType ?? null,
        channelKind: previous.channelKind ?? null,
      });
      const nextAvatarType = resolveChannelAvatarProfileType({
        ownerType: nextSubscription.ownerType ?? null,
        channelKind: nextSubscription.channelKind ?? null,
      });
      if (previousAvatarType === 'artist' && nextAvatarType !== 'artist') {
        nextSubscription.ownerType = 'artist';
      }

      if (shouldKeepPreviousStatus) {
        nextSubscription.isActive = previous.isActive;
        nextSubscription.isRecurring = previous.isRecurring;
        nextSubscription.isCancelled = previous.isCancelled;
        nextSubscription.statusLabel = previous.statusLabel;
        nextSubscription.nextBillingDate = previous.nextBillingDate ?? nextSubscription.nextBillingDate;
        nextSubscription.nextBillingLabel = previous.nextBillingLabel ?? nextSubscription.nextBillingLabel;
        nextSubscription.periodEndDate = previous.periodEndDate ?? nextSubscription.periodEndDate;
      }

      if (!nextSubscription.ownerUsername) {
        nextSubscription.ownerUsername = previous.ownerUsername ?? null;
      }

      merged.set(key, {
        ...nextSubscription,
      });
    });
    return Array.from(merged.values());
  }, [membershipChannelSubscriptions, payloadChannelSubscriptions]);
  const channelSubscriptionPaymentEntries = useMemo(
    () => buildChannelSubscriptionPaymentEntries(activeChannelSubscriptions),
    [activeChannelSubscriptions]
  );
  const allPaymentEntries = useMemo(
    () => mergeAndSortPaymentEntries(paymentEntries, channelSubscriptionPaymentEntries),
    [channelSubscriptionPaymentEntries, paymentEntries]
  );
  const paymentFilterOptions = useMemo(() => getPaymentFilterOptions(isGallery), [isGallery]);
  const filteredPaymentEntries = useMemo(() => (
    paymentFilter === PAYMENT_FILTER_OPTIONS[0]
      ? allPaymentEntries
      : allPaymentEntries.filter((entry) => entry.category === paymentFilter)
  ), [allPaymentEntries, paymentFilter]);
  const resolvePaymentInvoiceUrl = useCallback((entry) => {
    const directUrl = typeof entry?.invoiceUrl === 'string' && entry.invoiceUrl.trim()
      ? entry.invoiceUrl.trim()
      : null;
    if (directUrl) {
      return directUrl;
    }
    const orderId = String(entry?.orderId ?? '').trim();
    if (!orderId) {
      return null;
    }
    const paymentType = String(entry?.type ?? '').toLowerCase();
    if (paymentType === 'original') {
      return galleryInvoicesByOrderId[orderId] ?? null;
    }
    return suarteInvoicesByOrderId[orderId] ?? null;
  }, [galleryInvoicesByOrderId, suarteInvoicesByOrderId]);
  const hasActiveSupportSubscriptions = activeSupportSubscriptions.length > 0;

  const hasAnySubscription = hasPrimarySubscription
    || activeChannelSubscriptions.length > 0
    || hasActiveSupportSubscriptions;
  const resolvedInvoiceUrlForTarget = resolvePaymentInvoiceUrl(invoiceTarget);
  const supportCancelPopupAvatarImage = resolveUserProfileImage(
    supportCancellationTarget?.supportedUserProfileImage,
    supportCancellationTarget?.supportedUser?.user_image,
    supportCancellationTarget?.supportedUser,
  );
  const supportCancelPopupProfileType = resolveUserProfileTypeWithFallback(
    'collector',
    supportCancellationTarget?.supportedUserProfileType,
    supportCancellationTarget?.supportedUser?.user_type,
    supportCancellationTarget?.supportedUser?.__t,
    supportCancellationTarget?.supportedUser,
  );

  const togglePaymentActionMenu = useCallback((paymentId) => {
    const normalizedId = String(paymentId ?? '');
    setActivePaymentActionMenuId((current) => (current === normalizedId ? null : normalizedId));
  }, []);

  const openSummaryPopup = useCallback((entry) => {
    setActivePaymentActionMenuId(null);
    setSummaryTarget(entry ?? null);
  }, []);

  const openInvoicePopup = useCallback((entry) => {
    setActivePaymentActionMenuId(null);
    setInvoiceTarget(entry ?? null);
  }, []);

  const closeSummaryPopup = useCallback(() => {
    setSummaryTarget(null);
  }, []);

  const closeInvoicePopup = useCallback(() => {
    setInvoiceTarget(null);
    setInvoiceStatusMessage(null);
    setInvoiceBillingRequired(false);
  }, []);

  const handleOpenResolvedInvoice = useCallback(async (entry) => {
    setActivePaymentActionMenuId(null);
    setInvoiceStatusMessage(null);
    setInvoiceBillingRequired(false);
    const invoiceUrl = resolvePaymentInvoiceUrl(entry);
    const type = String(entry?.type ?? '').toLowerCase();
    const orderId = entry?.orderId ?? null;
    const orderNumber = entry?.orderNumber ?? null;

    if (invoiceUrl) {
      const separator = invoiceUrl.includes('?') ? '&' : '?';
      window.open(`${invoiceUrl}${separator}v=${Date.now()}`, '_blank', 'noopener,noreferrer');
      return;
    }

    if (type === 'original') {
      if (String(entry?.shippingStatus ?? '').toLowerCase() === 'completed') {
        const hasBillingReady = hasVerifiedInvoiceBilling(entry, profileData);
        if (!hasBillingReady) {
          setInvoiceBillingRequired(true);
          setInvoiceStatusMessage('Add and verify billing details to request this invoice.');
        } else {
          setInvoiceStatusMessage('This invoice is issued by the gallery. Request it from the gallery if still unavailable.');
        }
      } else {
        setInvoiceStatusMessage('Original invoice can be requested once the order is completed.');
      }
      openInvoicePopup(entry);
      return;
    }
    if (!orderId && !orderNumber) {
      setInvoiceStatusMessage('Invoice not available yet for this payment.');
      openInvoicePopup(entry);
      return;
    }

    const requestKey = String(orderId ?? orderNumber);
    setInvoiceActionLoadingOrderId(requestKey);
    try {
      const { response, data } = await invoicesService.requestSuarteInvoice({
        orderId: orderId ?? null,
        orderNumber: orderNumber ?? null,
      });
      if (response?.status === 409 && data?.billing_required) {
        setInvoiceBillingRequired(true);
        setInvoiceStatusMessage(data?.message ?? 'Add and verify billing details to request this invoice.');
        openInvoicePopup(entry);
        return;
      }
      const resolvedUrl = data?.invoice_url ?? invoiceUrl ?? null;
      if (!response?.ok || !resolvedUrl) {
        setInvoiceBillingRequired(false);
        setInvoiceStatusMessage(data?.message ?? 'Unable to generate invoice right now.');
        openInvoicePopup(entry);
        return;
      }
      const normalizedOrderId = String(orderId ?? data?.order_id ?? '');
      setSuarteInvoicesByOrderId((current) => ({
        ...current,
        ...(normalizedOrderId ? { [normalizedOrderId]: resolvedUrl } : {}),
      }));
      setInvoiceStatusMessage(null);
      const separator = resolvedUrl.includes('?') ? '&' : '?';
      window.open(`${resolvedUrl}${separator}v=${Date.now()}`, '_blank', 'noopener,noreferrer');
    } catch (_error) {
      setInvoiceBillingRequired(false);
      setInvoiceStatusMessage('Unable to generate invoice right now.');
      openInvoicePopup(entry);
    } finally {
      setInvoiceActionLoadingOrderId(null);
    }
  }, [openInvoicePopup, profileData, resolvePaymentInvoiceUrl]);

  const handleRequestGalleryInvoice = useCallback(async (entry) => {
    const isCompletedOriginal =
      String(entry?.type ?? '').toLowerCase() === 'original'
      && String(entry?.shippingStatus ?? '').toLowerCase() === 'completed';

    if (!isCompletedOriginal) {
      setInvoiceStatusMessage('Original invoice can be requested once the order is completed.');
      return;
    }

    if (!hasVerifiedInvoiceBilling(entry, profileData)) {
      setInvoiceBillingRequired(true);
      setInvoiceStatusMessage('Add and verify billing details to request this invoice.');
      return;
    }

    const requestKey = String(entry?.orderId ?? entry?.orderNumber ?? '');
    if (!requestKey) {
      setInvoiceStatusMessage('Order id is required to request this invoice.');
      return;
    }

    setInvoiceActionLoadingOrderId(requestKey);
    try {
      const { response, data } = await invoicesService.requestGalleryInvoice({
        orderId: entry?.orderId ?? null,
        orderNumber: entry?.orderNumber ?? null,
      });
      if (response?.status === 409 && data?.billing_required) {
        setInvoiceBillingRequired(true);
        setInvoiceStatusMessage(data?.error ?? data?.message ?? 'Add and verify billing details to request this invoice.');
        return;
      }
      if (!response?.ok) {
        setInvoiceStatusMessage(data?.error ?? data?.message ?? 'Unable to send invoice request right now.');
        return;
      }
      setInvoiceBillingRequired(false);
      setInvoiceStatusMessage(data?.message ?? 'Invoice request sent to the gallery.');
    } catch (_error) {
      setInvoiceStatusMessage('Unable to send invoice request right now.');
    } finally {
      setInvoiceActionLoadingOrderId(null);
    }
  }, [profileData]);

  const ensureStripeInstance = useCallback(async () => {
    if (stripePromise) {
      return stripePromise;
    }
    const publishableKey = await resolveStripePublishableKey();
    if (!publishableKey) {
      return null;
    }
    const promise = loadStripe(publishableKey);
    setStripePromise(promise);
    return promise;
  }, [stripePromise]);

  const openSupportCancelPopup = useCallback((subscription) => {
    setSupportCancellationTarget(subscription);
    setShowSupportCancelPopup(true);
  }, []);

  const closeSupportCancelPopup = useCallback(() => {
    setShowSupportCancelPopup(false);
    setSupportCancellationTarget(null);
  }, []);

  const handleSupportCancelConfirm = useCallback(async () => {
    const subscriptionId = supportCancellationTarget?.id ?? null;
    if (!subscriptionId) {
      closeSupportCancelPopup();
      return;
    }
    try {
      setSupportResumeError(null);
      const { response, data } = await SupportService.cancelSupportSubscription(subscriptionId);
      if (!response?.ok) {
        throw new Error(data?.message ?? 'Unable to cancel support subscription.');
      }
      refreshProfile();
    } catch (error) {
      setSupportResumeError(error?.message ?? 'Unable to cancel support subscription.');
    } finally {
      closeSupportCancelPopup();
    }
  }, [closeSupportCancelPopup, refreshProfile, supportCancellationTarget]);

  const handleSupportRestart = useCallback(async (subscription) => {
    const subscriptionId = subscription?.id ?? null;
    if (!subscriptionId) {
      return;
    }
    setSupportResumeError(null);
    try {
      const { response, data } = await SupportService.resumeSupportSubscription(subscriptionId);
      if (!response?.ok) {
        throw new Error(data?.message ?? 'Unable to restart support subscription.');
      }
      if (data?.requires_action && data?.client_secret) {
        const stripeInstance = await ensureStripeInstance();
        if (!stripeInstance) {
          throw new Error('Stripe is unavailable. Please try again in a moment.');
        }
        const confirmResult = await stripeInstance.confirmCardPayment(data.client_secret);
        if (confirmResult?.error) {
          throw confirmResult.error;
        }
      }
      refreshProfile();
    } catch (error) {
      setSupportResumeError(error?.message ?? 'Unable to restart support subscription.');
      console.error('[ProfileSubscriptions] failed to restart support subscription', error);
    }
  }, [ensureStripeInstance, refreshProfile]);

  const toggleChannelActionMenu = useCallback((channelId) => {
    if (!channelId) {
      return;
    }
    const normalizedChannelId = String(channelId);
    setActiveSupportActionMenuId(null);
    setActiveChannelActionMenuId((previous) => (previous === normalizedChannelId ? null : normalizedChannelId));
  }, []);

  const toggleSupportActionMenu = useCallback((subscriptionId) => {
    if (!subscriptionId) {
      return;
    }
    const normalizedSubscriptionId = String(subscriptionId);
    setActiveChannelActionMenuId(null);
    setActiveSupportActionMenuId((previous) => (previous === normalizedSubscriptionId ? null : normalizedSubscriptionId));
  }, []);

  const openChannelRevokePopup = useCallback((subscription) => {
    if (!subscription?.channelId) {
      return;
    }
    setActiveChannelActionMenuId(null);
    setChannelRevokeTarget(subscription);
    setChannelRevokePopupOpen(true);
  }, []);

  const closeChannelRevokePopup = useCallback(() => {
    if (revokingChannelSubscription) {
      return;
    }
    setChannelRevokePopupOpen(false);
    setChannelRevokeTarget(null);
  }, [revokingChannelSubscription]);

  const handleActivateChannelSubscription = useCallback(async (subscription) => {
    const channelId = subscription?.channelId ? String(subscription.channelId) : null;
    if (!channelId || activatingChannelId) {
      return;
    }

    setChannelMembershipError(null);
    setActiveChannelActionMenuId(null);
    setActivatingChannelId(channelId);
    try {
      const origin = typeof window !== 'undefined' ? window.location.origin : '';
      let ownerSlug = subscription?.ownerUsername
        ? String(subscription.ownerUsername).replace(/^@/, '').trim()
        : '';

      if (!ownerSlug) {
        try {
          const { response: channelResponse, data: channelData } = await ChannelsService.getOne(channelId);
          if (channelResponse?.ok) {
            const channel = channelData?.data && !Array.isArray(channelData.data)
              ? channelData.data
              : channelData;
            ownerSlug = String(
              channel?.owner_username
              ?? channel?.owner?.user_username
              ?? ''
            ).replace(/^@/, '').trim();
          }
        } catch (_) {
          ownerSlug = '';
        }
      }
      let successUrl;
      let cancelUrl;
      if (ownerSlug) {
        window.location.href = `/channel/${ownerSlug}/${channelId}/join`;
        return;
      } else {
        const currentPath = typeof window !== 'undefined' ? window.location.pathname : '/payments-profile';
        const successUrlObject = new URL(`${origin}${currentPath}`);
        successUrlObject.searchParams.set(CHANNEL_CHECKOUT_SUCCESS_FLAG, '1');
        successUrlObject.searchParams.set(CHANNEL_CHECKOUT_CHANNEL_ID_PARAM, channelId);
        const cancelUrlObject = new URL(`${origin}${currentPath}`);
        successUrl = successUrlObject.toString();
        cancelUrl = cancelUrlObject.toString();
      }

      const { response, data } = await ChannelsService.createCheckoutSession(channelId, {
        successUrl,
        cancelUrl,
      });
      if (!response?.ok || !data?.checkout_url) {
        throw new Error(data?.message ?? 'Unable to activate subscription right now.');
      }
      if (typeof window !== 'undefined') {
        window.location.href = data.checkout_url;
      }
    } catch (error) {
      setChannelMembershipError(error?.message ?? 'Unable to activate subscription right now.');
    } finally {
      setActivatingChannelId(null);
    }
  }, [activatingChannelId]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const params = new URLSearchParams(window.location.search || '');
    if (params.get(CHANNEL_CHECKOUT_SUCCESS_FLAG) !== '1') {
      return undefined;
    }

    const targetChannelId = params.get(CHANNEL_CHECKOUT_CHANNEL_ID_PARAM);
    if (!targetChannelId) {
      return undefined;
    }

    let cancelled = false;
    let attempts = 0;
    let timeoutId = null;
    const normalizedChannelId = String(targetChannelId);

    const clearCheckoutQueryParams = () => {
      try {
        const nextUrl = new URL(window.location.href);
        nextUrl.searchParams.delete(CHANNEL_CHECKOUT_SUCCESS_FLAG);
        nextUrl.searchParams.delete(CHANNEL_CHECKOUT_CHANNEL_ID_PARAM);
        window.history.replaceState({}, '', `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`);
      } catch (_) {
        // noop
      }
    };

    const checkMembershipAccess = async () => {
      attempts += 1;
      try {
        const { response, data } = await ChannelsService.getMemberOf('me');
        if (!cancelled && response?.ok) {
          const list = Array.isArray(data?.data) ? data.data : [];
          const matchedMembership = list.find((entry) => String(entry?._id ?? entry?.id) === normalizedChannelId);
          const isMember = Boolean(matchedMembership);
          if (isMember) {
            clearCheckoutQueryParams();
            setActivatingChannelId(null);
            const membershipOwnerUsername =
              matchedMembership?.owner_username
              ?? matchedMembership?.owner?.user_username
              ?? matchedMembership?.user_username
              ?? null;

            let ownerSlug = typeof membershipOwnerUsername === 'string'
              ? membershipOwnerUsername.replace(/^@/, '').trim()
              : '';

            if (!ownerSlug) {
              try {
                const { response: channelResponse, data: channelData } = await ChannelsService.getOne(normalizedChannelId);
                if (channelResponse?.ok) {
                  const channel = channelData?.data && !Array.isArray(channelData.data)
                    ? channelData.data
                    : channelData;
                  ownerSlug = String(
                    channel?.owner_username
                    ?? channel?.owner?.user_username
                    ?? ''
                  ).replace(/^@/, '').trim();
                }
              } catch (_) {
                // fallback to refresh below
              }
            }

            if (ownerSlug) {
              window.location.href = `/channel/${ownerSlug}/${normalizedChannelId}`;
              return;
            }

            refreshProfile();
            return;
          }
        }
      } catch (_) {
        // keep polling
      }

      if (attempts === 1 || attempts % 5 === 0) {
        try {
          await ChannelsService.ensureSubscription(normalizedChannelId);
        } catch (_) {
          // keep polling
        }
      }

      if (attempts >= CHANNEL_CHECKOUT_POLL_MAX_ATTEMPTS) {
        clearCheckoutQueryParams();
        setActivatingChannelId(null);
        refreshProfile();
        return;
      }

      timeoutId = setTimeout(checkMembershipAccess, CHANNEL_CHECKOUT_POLL_INTERVAL_MS);
    };

    setChannelMembershipError(null);
    setActivatingChannelId(normalizedChannelId);
    checkMembershipAccess();

    return () => {
      cancelled = true;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [refreshProfile]);

  const handleConfirmChannelRevoke = useCallback(async () => {
    const channelId = channelRevokeTarget?.channelId ? String(channelRevokeTarget.channelId) : null;
    if (!channelId) {
      setChannelRevokePopupOpen(false);
      setChannelRevokeTarget(null);
      return;
    }

    setRevokingChannelSubscription(true);
    setChannelMembershipError(null);
    try {
      const { response, data } = await ChannelsService.cancelSubscription(channelId, { immediate: true });
      if (!response?.ok) {
        throw new Error(data?.message ?? 'Could not revoke your subscription right now.');
      }
      setChannelRevokePopupOpen(false);
      setChannelRevokeTarget(null);
      refreshProfile();
    } catch (error) {
      setChannelMembershipError(error?.message ?? 'Could not revoke your subscription right now.');
    } finally {
      setRevokingChannelSubscription(false);
    }
  }, [channelRevokeTarget, refreshProfile]);

  const openBusinessBillingPopup = useCallback((target) => {
    if (!target) {
      return;
    }
    setBusinessBillingError(null);
    setBusinessBillingSuccessMessage(null);
    setBusinessBillingNeedsDetails(!hasVerifiedInvoiceBilling(target, profileData));
    setBusinessBillingTarget(target);
  }, [profileData]);

  const closeBusinessBillingPopup = useCallback(() => {
    if (businessBillingLoading) {
      return;
    }
    setBusinessBillingTarget(null);
    setBusinessBillingError(null);
    setBusinessBillingNeedsDetails(false);
    setBusinessBillingSuccessMessage(null);
  }, [businessBillingLoading]);

  const switchBusinessBillingRequest = useCallback(async (target, payload = {}) => {
    if (!target) {
      return { response: null, data: null };
    }
    if (target.kind === 'support') {
      return SupportService.switchSupportSubscriptionBusinessBilling(target.subscriptionId, payload);
    }
    if (target.kind === 'channel') {
      return ChannelsService.switchSubscriptionBusinessBilling(target.channelId, payload);
    }
    if (target.kind === 'suarte_plus') {
      return suartePlusService.switchBusinessBilling(target.subscriptionId, payload);
    }
    return { response: null, data: null };
  }, []);

  const handleConfirmBusinessBillingSwitch = useCallback(async () => {
    if (!businessBillingTarget || businessBillingLoading) {
      return;
    }

    setBusinessBillingError(null);
    setBusinessBillingSuccessMessage(null);
    setBusinessBillingLoading(true);
    try {
      const firstAttempt = await switchBusinessBillingRequest(businessBillingTarget);
      const { response, data } = firstAttempt ?? {};

      if (response?.status === 409 && data?.billing_required) {
        setBusinessBillingNeedsDetails(true);
        setBusinessBillingError(data?.message ?? 'Please complete verified business billing details first.');
        return;
      }

      if (response?.status === 202 && data?.status === 'requires_action' && data?.client_secret) {
        const stripeInstance = await ensureStripeInstance();
        if (!stripeInstance) {
          throw new Error('Stripe is unavailable. Please try again in a moment.');
        }

        const confirmResult = await stripeInstance.confirmCardSetup(data.client_secret);
        if (confirmResult?.error) {
          throw confirmResult.error;
        }

        const finalizeAttempt = await switchBusinessBillingRequest(
          businessBillingTarget,
          {
            setup_intent_id: data?.setup_intent_id ?? confirmResult?.setupIntent?.id ?? null,
            payment_method_id: data?.payment_method_id ?? confirmResult?.setupIntent?.payment_method ?? null,
          }
        );
        if (!finalizeAttempt?.response?.ok) {
          if (finalizeAttempt?.response?.status === 409 && finalizeAttempt?.data?.billing_required) {
            setBusinessBillingNeedsDetails(true);
          }
          throw new Error(finalizeAttempt?.data?.message ?? 'Unable to update business billing mode.');
        }
      } else if (!response?.ok) {
        throw new Error(data?.message ?? 'Unable to update business billing mode.');
      }

      setBusinessBillingTarget(null);
      setBusinessBillingNeedsDetails(false);
      setBusinessBillingError(null);
      setBusinessBillingSuccessMessage(null);
      stateHandler.set('temporalPopup', {
        text: 'Business billing secured. No charge now; update applies on the next renewal',
      });
      refreshProfile();
    } catch (error) {
      setBusinessBillingError(error?.message ?? 'Unable to update business billing mode.');
    } finally {
      setBusinessBillingLoading(false);
    }
  }, [
    businessBillingLoading,
    businessBillingTarget,
    ensureStripeInstance,
    refreshProfile,
    stateHandler,
    switchBusinessBillingRequest,
  ]);

  useEffect(() => {
    if (!suartePlusRenewalMessage) {
      return undefined;
    }
    const timeoutId = setTimeout(() => {
      setSuartePlusRenewalMessage(null);
    }, 4500);
    return () => clearTimeout(timeoutId);
  }, [suartePlusRenewalMessage]);

  const handleSuartePlusSuccess = useCallback(() => {
    refreshProfile();
  }, [refreshProfile]);

  const handleGalleryPriceResolved = useCallback((price) => {
    setGalleryCheckoutPrice(price ?? null);
  }, []);

  const handleGalleryPaymentSuccess = (payload) => {
    setShowGalleryPaymentModal(false);
    if (payload?.price) {
      setGalleryCheckoutPrice(payload.price);
    }
    refreshGallerySubscription();
  };

  const handleSeatPaymentSuccess = () => {
    setShowSeatPaymentModal(false);
    refreshGallerySubscription();
  };

  const handleActivateLifetimeSubscription = useCallback(async () => {
    if (!galleryLifetimeBenefit.isEligible || gallerySubscription || isActivatingLifetime) {
      return;
    }
    setLifetimeActivationError(null);
    setIsActivatingLifetime(true);
    try {
      const { response, data } = await gallerySubscriptionService.confirmPaymentMethod({
        plan: galleryPlanKey || DEFAULT_GALLERY_PLAN,
      });
      if (!response?.ok) {
        const message = data?.message ?? 'Unable to activate the free subscription.';
        throw new Error(message);
      }
      if (data?.price) {
        setGalleryCheckoutPrice(data.price);
      }
      refreshGallerySubscription();
    } catch (error) {
      setLifetimeActivationError(error?.message ?? 'Unable to activate the free subscription.');
    } finally {
      setIsActivatingLifetime(false);
    }
  }, [
    galleryLifetimeBenefit.isEligible,
    galleryPlanKey,
    gallerySubscription,
    isActivatingLifetime,
    refreshGallerySubscription,
  ]);

  const handleCancelSuartePlus = useCallback(async () => {
    if (!hasActiveSuartePlus || !suartePlusMembershipId || isCancellingSuartePlus) {
      return;
    }
    setSuartePlusCancellationError(null);
    setIsCancellingSuartePlus(true);
    try {
      const { response, data } = await suartePlusService.cancelSubscription(suartePlusMembershipId);
      if (!response?.ok) {
        throw new Error(data?.message ?? 'Unable to cancel the Suarte Plus subscription.');
      }
      refreshProfile();
    } catch (error) {
      setSuartePlusCancellationError(error?.message ?? 'Unable to cancel the Suarte Plus subscription.');
    } finally {
      setIsCancellingSuartePlus(false);
    }
  }, [hasActiveSuartePlus, isCancellingSuartePlus, refreshProfile, suartePlusMembershipId]);

  const handleResumeSuartePlus = useCallback(async () => {
    if (!canShowRenewAction || !suartePlusMembershipId || isRenewingSuartePlus) {
      return;
    }

    setSuartePlusCancellationError(null);
    setSuartePlusRenewalMessage(null);
    setIsRenewingSuartePlus(true);
    try {
      const firstAttempt = await suartePlusService.resumeSubscription(suartePlusMembershipId);
      const { response, data } = firstAttempt ?? {};
      if (response?.status === 202 && data?.status === 'requires_action' && data?.client_secret) {
        const stripeInstance = await ensureStripeInstance();
        if (!stripeInstance) {
          throw new Error('Stripe is unavailable. Please try again in a moment.');
        }
        const confirmResult = await stripeInstance.confirmCardSetup(data.client_secret);
        if (confirmResult?.error) {
          throw confirmResult.error;
        }

        const finalizeAttempt = await suartePlusService.resumeSubscription(
          suartePlusMembershipId,
          {
            setup_intent_id: data?.setup_intent_id ?? confirmResult?.setupIntent?.id ?? null,
            payment_method_id: data?.payment_method_id ?? confirmResult?.setupIntent?.payment_method ?? null,
          }
        );

        if (!finalizeAttempt?.response?.ok) {
          throw new Error(finalizeAttempt?.data?.message ?? 'Unable to renew the Suarte Plus subscription.');
        }
      } else if (!response?.ok) {
        throw new Error(data?.message ?? 'Unable to renew the Suarte Plus subscription.');
      }
      const nextBillingDateRaw =
        (firstAttempt?.data?.next_billing_date ?? firstAttempt?.data?.billingDate ?? null);
      const nextBillingDate = nextBillingDateRaw ? new Date(nextBillingDateRaw) : null;
      const nextBillingLabelForMessage =
        nextBillingDate instanceof Date && Number.isFinite(nextBillingDate.getTime())
          ? new Intl.DateTimeFormat('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          }).format(nextBillingDate)
          : null;

      setSuartePlusRenewalMessage(
        nextBillingLabelForMessage
          ? `Renewal secured. No charge now. Next charge on ${nextBillingLabelForMessage}.`
          : 'Renewal secured. No charge now. Your next charge will happen on the scheduled billing date.'
      );
      refreshProfile();
    } catch (error) {
      setSuartePlusCancellationError(error?.message ?? 'Unable to renew the Suarte Plus subscription.');
    } finally {
      setIsRenewingSuartePlus(false);
    }
  }, [canShowRenewAction, ensureStripeInstance, isRenewingSuartePlus, refreshProfile, suartePlusMembershipId]);

  const handleSwitchSuartePlusToBusiness = useCallback(() => {
    if (!suartePlusMembershipId || !hasActiveSuartePlus) {
      return;
    }
    openBusinessBillingPopup({
      kind: 'suarte_plus',
      subscriptionId: String(suartePlusMembershipId),
      id: String(suartePlusMembershipId),
      isActive: true,
      description: 'Suarte+ subscription',
      billingInfo: profileData?.auction_bidder_profile?.billing_info ?? null,
      billingAddress: profileData?.auction_bidder_profile?.billing_address ?? null,
    });
  }, [
    hasActiveSuartePlus,
    openBusinessBillingPopup,
    profileData?.auction_bidder_profile?.billing_address,
    profileData?.auction_bidder_profile?.billing_info,
    suartePlusMembershipId,
  ]);

  const goBackHandler = useGoBack('/profile');

  return (
    <div className="subscriptions-layout__page">
      <div className="subscriptions-layout__wrapper">
        <ProfileOptionsPageHeader title={t('title')} onBack={goBackHandler} />

        <div className="subscriptions-layout__content">
          <div className="profile-subscriptions">
            {profileError && (
              <Text className="profile-subscriptions__error">
                {profileError}
              </Text>
            )}

            {subscriptionLoading && !hasAnySubscription ? (
              <div className="profile-subscriptions__loading-state">
                <Loader inline className="profile-subscriptions__loader" />
              </div>
            ) : hasPrimarySubscription ? (
              <>
                <SuartePlusSubscriptionCard
                  t={t}
                  isFoundingGallery={isFoundingGallery}
                  showCancelButton={showSuartePlusCancelButton}
                  cancelDisabled={isCancellingSuartePlus || subscriptionLoading || isSuartePlusCancellationScheduled}
                  onOpenCancel={() => setShowCancelSuartePlusPopup(true)}
                  cancelButtonLabel={cancelButtonLabel}
                  nextBillingLabel={nextBillingLabel}
                  amountLabel={amountLabel}
                  isGallery={isGallery}
                  seatsSummary={seatsSummary}
                  seatCostLabel={seatCostLabel}
                  galleryTotalLabel={galleryTotalLabel}
                  showBusinessBillingAction={hasActiveSuartePlus && !isGallery && !isSuartePlusBusinessBilling}
                  businessBillingLoading={businessBillingLoading && businessBillingTarget?.kind === 'suarte_plus'}
                  onSwitchBusinessBilling={handleSwitchSuartePlusToBusiness}
                  cancellationMessage={cancellationMessage}
                  canRenew={canShowRenewAction}
                  renewing={isRenewingSuartePlus}
                  onRenew={handleResumeSuartePlus}
                />
                {suartePlusCancellationError && (
                  <Text className="profile-subscriptions__error">
                    {suartePlusCancellationError}
                  </Text>
                )}
                {suartePlusRenewalMessage && (
                  <Text className="profile-subscriptions__cta-note">
                    {suartePlusRenewalMessage}
                  </Text>
                )}
              </>
            ) : !hasAnySubscription ? (
              <div className="profile-subscriptions__empty-state">
                <SuartePlusShortIcon className="profile-subscriptions__empty-icon" aria-hidden="true" />
                <Text className="profile-subscriptions__empty-message">
                  {t('empty.subscriptions')}
                </Text>
              </div>
            ) : null}

            {activeChannelSubscriptions.length > 0 && (
              <div className="profile-subscriptions__section">
                <Text className="profile-subscriptions__section-title">
                  {t('sections.channels')}
                </Text>
                <div className="profile-channel-subscriptions">
                  {activeChannelSubscriptions.map((subscription) => (
                    <ChannelSubscriptionCard
                      key={`channel-subscription-${subscription.channelId}`}
                      subscription={subscription}
                      isMenuOpen={activeChannelActionMenuId === String(subscription.channelId)}
                      onToggleMenu={toggleChannelActionMenu}
                      onActivate={handleActivateChannelSubscription}
                      onCancel={openChannelRevokePopup}
                      onSwitchBusinessBilling={(item) => openBusinessBillingPopup({
                        ...item,
                        kind: 'channel',
                        channelId: item?.channelId ? String(item.channelId) : null,
                      })}
                      activating={activatingChannelId === String(subscription.channelId)}
                    />
                  ))}
                </div>
              </div>
            )}

            {channelMembershipError && (
              <Text className="profile-subscriptions__error">
                {channelMembershipError}
              </Text>
            )}

            {hasActiveSupportSubscriptions && (
              <div className="profile-subscriptions__section">
                <Text className="profile-subscriptions__section-title">
                  {t('sections.support')}
                </Text>
                <div className="profile-support-subscriptions">
                  {activeSupportSubscriptions.map((subscription) => (
                    <SupportSubscriptionCard
                      key={subscription.id}
                      subscription={subscription}
                      isMenuOpen={activeSupportActionMenuId === String(subscription.id)}
                      onToggleMenu={toggleSupportActionMenu}
                      onRequestCancel={openSupportCancelPopup}
                      onRequestRestart={handleSupportRestart}
                      onSwitchBusinessBilling={(item) => openBusinessBillingPopup({
                        ...item,
                        kind: 'support',
                        subscriptionId: item?.id ? String(item.id) : null,
                      })}
                    />
                  ))}
                  {supportResumeError && (
                    <Text className="profile-support-subscriptions__error ">
                      {supportResumeError}
                    </Text>
                  )}
                </div>
              </div>
            )}

            {showSuartePlusUpsell && (
              <div className="profile-subscriptions__cta">
                <ContinueButton onClick={() => setShowSuartePlusPopup(true)}>
                  {t('actions.get_suarte_plus')}
                </ContinueButton>
              </div>
            )}

            <GallerySubscriptionCta
              t={t}
              visible={isGallery && !subscriptionLoading}
              requiresPaymentMethod={requiresGalleryPaymentMethod}
              hasSubscription={Boolean(gallerySubscription)}
              paymentHint={galleryPaymentHint}
              onOpenPaymentMethod={() => setShowGalleryPaymentModal(true)}
              needsSeatPaymentMethod={needsSeatPaymentMethod}
              onOpenSeatPaymentMethod={() => setShowSeatPaymentModal(true)}
              lifetimeEligible={galleryLifetimeBenefit.isEligible}
              onActivateLifetime={handleActivateLifetimeSubscription}
              activatingLifetime={isActivatingLifetime}
              lifetimeActivationError={lifetimeActivationError}
            />

            {galleryError && (
              <Text className="profile-subscriptions__error">
                {galleryError}
              </Text>
            )}
            <PaymentsSection
              t={t}
              filter={paymentFilter}
              filterOptions={paymentFilterOptions}
              onFilterChange={setPaymentFilter}
              error={userPaymentsError}
              loading={paymentsLoading}
              entries={filteredPaymentEntries}
              activeActionMenuId={activePaymentActionMenuId}
              onToggleActionMenu={togglePaymentActionMenu}
              onOpenSummary={openSummaryPopup}
              onOpenInvoice={handleOpenResolvedInvoice}
              invoiceActionLoadingOrderId={invoiceActionLoadingOrderId}
            />
          </div>
        </div>
      </div>
      <PaymentSummaryDialog entry={summaryTarget} onClose={closeSummaryPopup} />
      <InvoiceDialog
        entry={invoiceTarget}
        onClose={closeInvoicePopup}
        statusMessage={invoiceStatusMessage}
        billingRequired={invoiceBillingRequired}
        onBillingSaved={() => {
          setInvoiceBillingRequired(false);
          setInvoiceStatusMessage('Billing details saved. Generating invoice...');
          if (invoiceTarget) {
            handleOpenResolvedInvoice({ ...invoiceTarget, invoiceBillingRequired: false });
          }
        }}
        invoiceUrl={resolvedInvoiceUrlForTarget}
        onOpenInvoice={handleOpenResolvedInvoice}
        onRequestGalleryInvoice={handleRequestGalleryInvoice}
        isGalleryInvoiceDisabled={
          invoiceActionLoadingOrderId === String(invoiceTarget?.orderId ?? invoiceTarget?.orderNumber ?? '')
          || !hasVerifiedInvoiceBilling(invoiceTarget, profileData)
        }
        isGalleryInvoiceLoading={
          invoiceActionLoadingOrderId === String(invoiceTarget?.orderId ?? invoiceTarget?.orderNumber ?? '')
        }
      />
      <BusinessBillingDialog
        target={businessBillingTarget}
        onClose={closeBusinessBillingPopup}
        profile={profileData}
        error={businessBillingError}
        successMessage={businessBillingSuccessMessage}
        needsDetails={businessBillingNeedsDetails}
        onDetailsSaved={() => {
          setBusinessBillingNeedsDetails(false);
          refreshProfile();
        }}
        loading={businessBillingLoading}
        onConfirm={handleConfirmBusinessBillingSwitch}
      />
      <SuartePlusPopup
        open={showSuartePlusPopup}
        onClose={() => setShowSuartePlusPopup(false)}
        onSuccess={handleSuartePlusSuccess}
      />
      <SupportCancellationDialog
        open={showSupportCancelPopup}
        target={supportCancellationTarget}
        avatarImage={supportCancelPopupAvatarImage}
        avatarProfileType={supportCancelPopupProfileType}
        onClose={closeSupportCancelPopup}
        onConfirm={handleSupportCancelConfirm}
      />
      <ChannelRevocationDialog
        open={channelRevokePopupOpen}
        target={channelRevokeTarget}
        onClose={closeChannelRevokePopup}
        loading={revokingChannelSubscription}
        onConfirm={handleConfirmChannelRevoke}
      />
      <SuartePlusCancellationDialog
        open={showCancelSuartePlusPopup}
        onClose={() => setShowCancelSuartePlusPopup(false)}
        loading={isCancellingSuartePlus}
        onConfirm={() => {
          setShowCancelSuartePlusPopup(false);
          handleCancelSuartePlus();
        }}
      />
      {isGallery && requiresGalleryPaymentMethod && (
        <GallerySubscriptionPaymentModal
          open={showGalleryPaymentModal}
          onClose={() => setShowGalleryPaymentModal(false)}
          onSuccess={handleGalleryPaymentSuccess}
          onPriceResolved={handleGalleryPriceResolved}
          priceHint={galleryPaymentHint}
          plan={galleryPlanKey}
          stripeSubscriptionId={
            gallerySubscription?.metadata?.stripe_subscription_id ??
              gallerySubscription?.stripe_subscription_id ??
              null
          }
        />
      )}
      {isGallery && needsSeatPaymentMethod && (
        <GallerySubscriptionPaymentModal
          open={showSeatPaymentModal}
          onClose={() => setShowSeatPaymentModal(false)}
          onSuccess={handleSeatPaymentSuccess}
          priceHint="Save a payment method for member seats."
          plan={galleryPlanKey}
          seatOnly
        />
      )}
      <TemporalPopup />
    </div>
  );
}
